import { Box, Button, IconButton, Typography, Skeleton } from "@mui/material";
import { useEffect, useState } from "react";
import { useListFeaturedDocuments } from "services/documents";
import LinkRouter from "components/navigation/LinkRouter";
import { useAdvisorProfilePath } from "components/common/CurrentUserAvatar";
import { ListFeaturedDocumentsResponse_FeaturedDocument } from "protogen/documents_service_pb";
import { DocumentFavoriteIcon } from "./DocumentFavoriteIcon";
import FlexBox from "components/common/FlexBox";
import Slider from "react-slick";
import { ChevronRight, ChevronLeft, ArrowRight } from "lucide-react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import useIsMobile from "components/hooks/useIsMobile";
import useIsDesktop from "components/hooks/useIsDesktop";
import { useNavigate } from "react-router-dom";

const CustomArrow = ({
  onClick,
  icon,
  isPrev,
  show,
}: {
  onClick?: React.MouseEventHandler<any> | undefined;
  icon: React.ReactNode;
  isPrev?: boolean;
  show: boolean;
}) => {
  if (!show) return null;
  return (
    <IconButton
      onClick={onClick}
      style={{
        position: "absolute",
        right: isPrev ? "auto" : "0px",
        left: isPrev ? "-12px" : "auto",
        top: "40%",
        zIndex: 2,
        backgroundColor: "white",
        borderRadius: "50px",
        border: "1px solid #D4D4D4",
        padding: "2px",
      }}
    >
      {icon}
    </IconButton>
  );
};

const SkeletonDocument = () => {
  const isMobile = useIsMobile();

  return (
    <FlexBox
      variant="contained"
      direction="column"
      gap={isMobile ? "0" : "4px"}
      sx={{
        marginRight: "12px",
        padding: isMobile ? "8px 16px" : "12px 20px",
        width: "198px",
      }}
    >
      <Skeleton
        variant="rectangular"
        width="100%"
        height={isMobile ? 36 : 48}
        sx={{ borderRadius: "4px", marginBottom: "8px" }}
      />

      <FlexBox
        stackedMobile
        alignItems={isMobile ? "start" : "end"}
        justifyContent="space-between"
        gap="4px"
      >
        <Skeleton
          variant="text"
          width="70%"
          height={16}
          sx={{ padding: "2px 0" }}
        />
        {!isMobile && (
          <Skeleton
            variant="rectangular"
            width={32}
            height={20}
            sx={{ borderRadius: "4px" }}
          />
        )}
      </FlexBox>
    </FlexBox>
  );
};

const DocumentCarousel = ({
  documents,
  loading = false,
}: {
  documents: ListFeaturedDocumentsResponse_FeaturedDocument[];
  loading?: boolean;
}) => {
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const [currentSlide, setCurrentSlide] = useState(0);
  const slidesToShow = isMobile ? 2 : isDesktop ? 4 : 3;

  const settings = {
    centerMode: false,
    infinite: false,
    speed: 250,
    arrows: true,
    variableWidth: false,
    slidesToShow: slidesToShow,
    beforeChange: (oldIndex: number, newIndex: number) => {
      setCurrentSlide(newIndex);
    },
    nextArrow: (
      <CustomArrow
        icon={<ChevronRight size={20} />}
        isPrev={false}
        show={!loading && currentSlide * slidesToShow < documents.length - 1}
      />
    ),
    prevArrow: (
      <CustomArrow
        icon={<ChevronLeft size={20} />}
        isPrev={true}
        show={!loading && currentSlide > 0}
      />
    ),
  };

  // Show skeleton loading state
  if (loading) {
    return (
      <Box sx={{}}>
        <SkeletonDocument />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        "& .slick-track": {
          display: "flex",
          alignItems: "stretch",
        },
        "& .slick-slide ": {
          height: "auto",
          div: {
            height: "100%",
          },
        },
      }}
    >
      <Slider {...settings}>
        {documents.map((doc, i) => (
          <FeaturedDocument key={i} doc={doc} />
        ))}
      </Slider>
    </Box>
  );
};

const FeaturedDocument = ({
  doc,
}: {
  doc: ListFeaturedDocumentsResponse_FeaturedDocument;
}) => {
  const getPath = useAdvisorProfilePath();
  const profilePath = getPath(doc.document?.owner?.ref || "");
  const isMobile = useIsMobile();

  return (
    <FlexBox
      variant="contained"
      direction="column"
      gap={isMobile ? "0" : "4px"}
      sx={{ marginRight: "12px", padding: isMobile ? "8px 16px" : "12px 20px" }}
    >
      <LinkRouter to={`/documents/${doc.document?.uuid}`}>
        <Typography
          variant="bodyHeavy"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {doc.document?.title}
        </Typography>
      </LinkRouter>

      <FlexBox
        stackedMobile
        alignItems={isMobile ? "start" : "end"}
        justifyContent="space-between"
        gap="4px"
      >
        <LinkRouter to={profilePath} sx={{ padding: "2px 0" }}>
          <Typography variant="bodySmall" color="text.secondary">
            By {doc.document?.owner?.firstName} {doc.document?.owner?.lastName}
          </Typography>
        </LinkRouter>
        {!isMobile && (
          <DocumentFavoriteIcon document={doc.document!} showCount={true} />
        )}
      </FlexBox>
    </FlexBox>
  );
};

export default () => {
  const [selectedSet] = useState<string | null>(null);
  const { request, data, loading } = useListFeaturedDocuments();
  useEffect(() => {
    request({
      anyTopics: selectedSet ? [selectedSet] : [],
      favoritesOnly: false,
    });
  }, [selectedSet]);
  const documentsToShow =
    data?.featuredDocuments
      .sort((a, b) => {
        return (
          Number(b.document?.favoriteCount || 0) -
          Number(a.document?.favoriteCount || 0)
        );
      })
      .slice(0, 10) || [];
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  return (
    <FlexBox
      direction="column"
      sx={{ marginBottom: isMobile ? "12px" : "24px", width: "100%" }}
    >
      <FlexBox
        justifyContent="space-between"
        alignItems={isMobile ? "start" : "center"}
      >
        <FlexBox
          stackedMobile
          gap={isMobile ? "4px" : "12px"}
          alignItems={isMobile ? "start" : "end"}
        >
          <Typography variant="h3">Community documents</Typography>
          <Typography variant="bodySmall" color="text.secondary">
            Most recent
          </Typography>
        </FlexBox>
        <Button
          variant="text"
          color="primary"
          sx={{ height: "unset" }}
          endIcon={<ArrowRight size={20} />}
          onClick={() => {
            navigate("/documents/community");
          }}
        >
          See all
        </Button>
      </FlexBox>
      <DocumentCarousel documents={documentsToShow} loading={loading} />
    </FlexBox>
  );
};
